import { useEffect, useRef } from "react";

/**
 * A thin, always-on-top strip of live prices — TradingView's free "Ticker
 * Tape" widget. TradingView's script builds its own iframe inside the
 * container div; nothing here fetches or stores market data.
 *
 * This is the one place on the site that needs s3.tradingview.com allowed in
 * the CSP (script-src to load the widget script, frame-src for the iframe it
 * creates). See index.html, public/_headers and
 * infra/cloudfront/response-headers-policy.json — src/lib/csp.test.ts fails
 * if those three drift apart.
 */
const SYMBOLS = [
  { proName: "NASDAQ:AAPL", title: "Apple" },
  { proName: "NASDAQ:TSLA", title: "Tesla" },
  { proName: "NASDAQ:NVDA", title: "NVIDIA" },
  { proName: "NASDAQ:MSFT", title: "Microsoft" },
  { proName: "AMEX:SPY", title: "S&P 500" },
  { proName: "BITSTAMP:BTCUSD", title: "Bitcoin" },
  { proName: "BITSTAMP:ETHUSD", title: "Ethereum" },
  { proName: "FX:EURUSD", title: "EUR/USD" },
  { proName: "FX:GBPUSD", title: "GBP/USD" },
];

const mount = (container: HTMLDivElement) => {
  const script = document.createElement("script");
  script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
  script.type = "text/javascript";
  script.async = true;
  script.innerHTML = JSON.stringify({
    symbols: SYMBOLS,
    showSymbolLogo: true,
    isTransparent: false,
    displayMode: "adaptive",
    colorTheme: "dark",
    locale: "en",
  });
  container.appendChild(script);
};

const MarketTicker = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // The widget is a third-party script plus an iframe, and nothing above
    // the fold depends on it. It is started once the page has had its first
    // paint and a quiet moment, instead of competing with the app's own
    // start-up for the main thread.
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      mount(container);
    };
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
    const handle = idle ? idle(start, { timeout: 2500 }) : window.setTimeout(start, 1200);

    // The widget's own iframe doesn't unmount with React, so clear the
    // container by hand if this ever stops rendering.
    return () => {
      started = true;
      if (idle) (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(handle);
      else window.clearTimeout(handle);
      container.innerHTML = "";
    };
  }, []);


  return (
    <div
      data-testid="lp-market-ticker"
      // The strip keeps its final height before the widget arrives. Its iframe
      // is 78px tall, and with no room reserved it pushed the whole page down
      // when it loaded: the header and hero jumped, which PageSpeed scored as a
      // layout shift of 0.10 on /lp. Same dark colour as the widget, so a
      // couple of spare pixels are not visible.
      className="sticky top-0 z-50 h-[78px] w-full overflow-hidden border-b border-white/10 bg-[#0a0a0a]"
    >
      <div ref={containerRef} className="tradingview-widget-container">
        <div className="tradingview-widget-container__widget" />
      </div>
    </div>
  );
};

export default MarketTicker;
